import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, TextField, Alert, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, Tabs, Tab, MenuItem, Paper,
} from '@mui/material'
import { Add, Undo } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listCashAccounts, type CashAccount } from '../../api/financiar'
import {
  listBankStatementImports, getBankStatementLines, getMatchCandidates, importBankStatement, reconcileLine, undoReconcile,
  categorizeLine, getCashPositionReport, BANK_TRANSACTION_CATEGORY_LABEL,
  type BankStatementImport, type BankStatementLine, type BankMatchCandidate, type BankTransactionCategory,
} from '../../api/reconciliere'
import { errorMessage } from '../../api/client'

function firstDayOfMonth(): string {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10)
}
function today(): string {
  return new Date().toISOString().slice(0, 10)
}

export default function ReconciliereBancaraPage() {
  const [tab, setTab] = useState(0)
  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Financiar</Typography>
        <Typography variant="inherit" color="text.primary">Reconciliere bancară</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Reconciliere bancară</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Extrase și potrivire" />
        <Tab label="Poziție de cash" />
      </Tabs>
      {tab === 0 ? <ExtraseTab /> : <PozitieCashTab />}
    </Stack>
  )
}

// ---------- Extrase și potrivire (FR-FCF-010/011/013) ----------

function ExtraseTab() {
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [linesTarget, setLinesTarget] = useState<BankStatementImport | null>(null)
  const { data, isLoading } = useQuery({ queryKey: ['financiar', 'reconciliere', 'extrase'], queryFn: listBankStatementImports })

  const columns = useMemo<GridColDef<BankStatementImport>[]>(() => [
    { field: 'cashAccountName', headerName: 'Cont', width: 200 },
    { field: 'fileName', headerName: 'Fișier', flex: 1, minWidth: 200 },
    { field: 'importedAt', headerName: 'Dată import', width: 160, valueFormatter: (v: string) => new Date(v).toLocaleString('ro-RO') },
    { field: 'importedByUserName', headerName: 'Importat de', width: 160 },
    { field: 'lineCount', headerName: 'Linii', width: 90, type: 'number' },
    {
      field: 'reconciledCount', headerName: 'Rezolvate', width: 130,
      renderCell: (p) => <Chip size="small" label={`${p.row.reconciledCount} / ${p.row.lineCount}`}
        color={p.row.reconciledCount === p.row.lineCount ? 'success' : 'default'} variant="outlined" />,
    },
    { field: 'skippedRowCount', headerName: 'Rânduri sărite', width: 130, type: 'number' },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setImportDialogOpen(true)}>Import extras</Button>
      </Box>
      <Box sx={{ height: 480, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          onRowClick={(p) => setLinesTarget(p.row)}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <ImportDialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} />
      <LinesDialog target={linesTarget} onClose={() => setLinesTarget(null)} />
    </Stack>
  )
}

// ---------- Import extras (FR-FCF-010, CSV) ----------

function ImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [cashAccountId, setCashAccountId] = useState<string | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const { data: accounts } = useQuery({ queryKey: ['financiar', 'conturi'], queryFn: listCashAccounts })

  const mutation = useMutation({
    mutationFn: () => importBankStatement(cashAccountId!, file!),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['financiar', 'reconciliere', 'extrase'] }) },
  })

  const handleClose = () => { setCashAccountId(null); setFile(null); mutation.reset(); onClose() }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import extras bancar</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Alert severity="info">
            Format CSV așteptat: <code>Data,Descriere,Referință,Sumă</code> (o linie de antet + câte un rând per tranzacție;
            sumă pozitivă = intrare, negativă = ieșire).
          </Alert>
          <Autocomplete
            options={accounts?.map((a) => a.id) ?? []}
            getOptionLabel={(id) => accounts?.find((a: CashAccount) => a.id === id)?.name ?? id}
            value={cashAccountId} onChange={(_, v) => setCashAccountId(v)}
            renderInput={(params) => <TextField {...params} label="Cont" />}
          />
          <Button component="label" variant="outlined">
            {file ? file.name : 'Alege fișier CSV'}
            <input type="file" accept=".csv,text/csv" hidden onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </Button>
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          {mutation.isSuccess && (
            <Alert severity="success">
              Import reușit: {mutation.data.lineCount} linii importate
              {mutation.data.skippedRowCount > 0 ? `, ${mutation.data.skippedRowCount} rânduri sărite (invalide)` : ''}.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose}>Închide</Button>
        <Button variant="contained" disabled={!cashAccountId || !file || mutation.isPending} onClick={() => mutation.mutate()}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Importă
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ---------- Linii de extras + reconciliere (FR-FCF-011) ----------

const STATUS_COLOR: Record<string, 'success' | 'info' | 'default'> = { Reconciliata: 'success', Categorisita: 'info', Nereconciliata: 'default' }
const STATUS_LABEL: Record<string, string> = { Reconciliata: 'Reconciliată', Categorisita: 'Categorisită', Nereconciliata: 'Nereconciliată' }

function LinesDialog({ target, onClose }: { target: BankStatementImport | null; onClose: () => void }) {
  const open = target !== null
  const [candidatesTarget, setCandidatesTarget] = useState<BankStatementLine | null>(null)
  const [categorizeTarget, setCategorizeTarget] = useState<BankStatementLine | null>(null)
  const { data, isLoading } = useQuery({
    queryKey: ['financiar', 'reconciliere', 'linii', target?.id],
    queryFn: () => getBankStatementLines(target!.id), enabled: open,
  })
  const qc = useQueryClient()
  const undoMutation = useMutation({
    mutationFn: (lineId: string) => undoReconcile(lineId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['financiar', 'reconciliere', 'linii', target?.id] })
      void qc.invalidateQueries({ queryKey: ['financiar', 'reconciliere', 'extrase'] })
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Linii extras — {target?.fileName}</DialogTitle>
      <DialogContent>
        {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
        {data && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Dată</TableCell><TableCell>Descriere</TableCell><TableCell>Referință</TableCell>
                <TableCell align="right">Sumă</TableCell><TableCell>Status</TableCell><TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.transactionDate}</TableCell>
                  <TableCell>{l.description}</TableCell>
                  <TableCell>{l.reference ?? '—'}</TableCell>
                  <TableCell align="right" sx={{ color: l.amount < 0 ? 'error.main' : 'success.main' }}>{l.amount.toFixed(2).replace('.', ',')}</TableCell>
                  <TableCell>
                    <Chip size="small" label={STATUS_LABEL[l.status]} color={STATUS_COLOR[l.status]} variant="outlined" />
                    {l.status === 'Reconciliata' && (l.matchedReceiptNumber ?? l.matchedPaymentNumber) && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {l.matchedReceiptNumber ?? l.matchedPaymentNumber}
                      </Typography>
                    )}
                    {l.status === 'Categorisita' && l.category && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {BANK_TRANSACTION_CATEGORY_LABEL[l.category]}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {l.status === 'Nereconciliata'
                      ? (
                        <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                          <Button size="small" onClick={() => setCandidatesTarget(l)}>Reconciliază</Button>
                          <Button size="small" onClick={() => setCategorizeTarget(l)}>Categorizează</Button>
                        </Stack>
                      )
                      : <Button size="small" startIcon={<Undo fontSize="small" />} onClick={() => undoMutation.mutate(l.id)}>Anulează</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>
      <CandidatesDialog target={candidatesTarget} onClose={() => setCandidatesTarget(null)} importId={target?.id} />
      <CategorizeDialog target={categorizeTarget} onClose={() => setCategorizeTarget(null)} importId={target?.id} />
    </Dialog>
  )
}

// ---------- Categorizare — cale de rezolvare alternativă la potrivire (FR-FCF-013) ----------

function CategorizeDialog({ target, importId, onClose }: { target: BankStatementLine | null; importId: string | undefined; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const [category, setCategory] = useState<BankTransactionCategory | ''>('')
  const [notes, setNotes] = useState('')

  const mutation = useMutation({
    mutationFn: () => categorizeLine(target!.id, { category: category as BankTransactionCategory, notes: notes || null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['financiar', 'reconciliere', 'linii', importId] })
      void qc.invalidateQueries({ queryKey: ['financiar', 'reconciliere', 'extrase'] })
      handleClose()
    },
  })

  const handleClose = () => { setCategory(''); setNotes(''); mutation.reset(); onClose() }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Categorizează linia — {target?.description}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField select label="Categorie" value={category} onChange={(e) => setCategory(e.target.value as BankTransactionCategory)}>
            {Object.entries(BANK_TRANSACTION_CATEGORY_LABEL).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </TextField>
          <TextField label="Notă (opțional)" value={notes} onChange={(e) => setNotes(e.target.value)} multiline minRows={2} />
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose}>Renunță</Button>
        <Button variant="contained" disabled={!category || mutation.isPending} onClick={() => mutation.mutate()}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Salvează
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ---------- Candidați de potrivire — confirmare explicită, niciodată automată ----------

function CandidatesDialog({ target, importId, onClose }: { target: BankStatementLine | null; importId: string | undefined; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['financiar', 'reconciliere', 'sugestii', target?.id],
    queryFn: () => getMatchCandidates(target!.id), enabled: open,
  })
  const mutation = useMutation({
    mutationFn: (c: BankMatchCandidate) => reconcileLine(target!.id, { receiptId: c.receiptId, paymentId: c.paymentId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['financiar', 'reconciliere', 'linii', importId] })
      void qc.invalidateQueries({ queryKey: ['financiar', 'reconciliere', 'extrase'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Candidați de potrivire</DialogTitle>
      <DialogContent>
        {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
        {data && data.length === 0 && <Alert severity="info">Niciun candidat propus pentru această linie.</Alert>}
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
        {data && data.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Document</TableCell><TableCell>Partener</TableCell><TableCell>Dată</TableCell>
                <TableCell align="right">Sumă</TableCell><TableCell align="right">Scor</TableCell><TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((c, i) => (
                <TableRow key={i}>
                  <TableCell>{c.receiptNumber ?? c.paymentNumber}</TableCell>
                  <TableCell>{c.partnerName}</TableCell>
                  <TableCell>{c.date}</TableCell>
                  <TableCell align="right">{c.amount.toFixed(2).replace('.', ',')}</TableCell>
                  <TableCell align="right">{c.score}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="contained" disabled={mutation.isPending} onClick={() => mutation.mutate(c)}>Selectează</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>
    </Dialog>
  )
}

// ---------- Poziție de cash ERP ↔ bancă (FR-FCF-012) ----------

function PozitieCashTab() {
  const [cashAccountId, setCashAccountId] = useState<string | null>(null)
  const [from, setFrom] = useState(firstDayOfMonth())
  const [to, setTo] = useState(today())
  const { data: accounts } = useQuery({ queryKey: ['financiar', 'conturi'], queryFn: listCashAccounts })
  const { data, isLoading } = useQuery({
    queryKey: ['financiar', 'reconciliere', 'pozitie', cashAccountId, from, to],
    queryFn: () => getCashPositionReport(cashAccountId!, from, to),
    enabled: !!cashAccountId && from <= to,
  })

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
        <Autocomplete
          sx={{ width: 260 }} options={accounts?.map((a) => a.id) ?? []}
          getOptionLabel={(id) => accounts?.find((a: CashAccount) => a.id === id)?.name ?? id}
          value={cashAccountId} onChange={(_, v) => setCashAccountId(v)}
          renderInput={(params) => <TextField {...params} label="Cont" />}
        />
        <TextField label="De la" type="date" size="small" value={from} onChange={(e) => setFrom(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField label="Până la" type="date" size="small" value={to} onChange={(e) => setTo(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
      </Stack>
      {from > to && <Alert severity="error">Data de final trebuie să fie după data de start.</Alert>}
      {!cashAccountId && <Alert severity="info">Alegeți un cont pentru a vedea poziția de cash.</Alert>}
      {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
      {data && (
        <>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell /><TableCell align="right">ERP</TableCell><TableCell align="right">Extras bancar</TableCell></TableRow>
              </TableHead>
              <TableBody>
                <TableRow><TableCell>Sold inițial</TableCell><TableCell align="right" colSpan={2} sx={{ textAlign: 'right' }}>{data.soldInitial.toFixed(2).replace('.', ',')}</TableCell></TableRow>
                <TableRow><TableCell>+ Intrări</TableCell><TableCell align="right">{data.totalIntrariErp.toFixed(2).replace('.', ',')}</TableCell><TableCell align="right">{data.totalIntrariExtras.toFixed(2).replace('.', ',')}</TableCell></TableRow>
                <TableRow><TableCell>− Ieșiri</TableCell><TableCell align="right">{data.totalIesiriErp.toFixed(2).replace('.', ',')}</TableCell><TableCell align="right">{data.totalIesiriExtras.toFixed(2).replace('.', ',')}</TableCell></TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>= Sold final</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{data.soldFinalErp.toFixed(2).replace('.', ',')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: data.soldFinalErp !== data.soldFinalBancar ? 'error.main' : undefined }}>
                    {data.soldFinalBancar.toFixed(2).replace('.', ',')}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Înregistrări ERP fără corespondent bancar ({data.inregistrariErpFaraCorespondent.length})</Typography>
            {data.inregistrariErpFaraCorespondent.length === 0
              ? <Typography variant="body2" color="text.secondary">Toate încasările/plățile perioadei au un corespondent în extras.</Typography>
              : (
                <Table size="small">
                  <TableHead><TableRow><TableCell>Tip</TableCell><TableCell>Document</TableCell><TableCell>Partener</TableCell><TableCell>Dată</TableCell><TableCell align="right">Sumă</TableCell></TableRow></TableHead>
                  <TableBody>
                    {data.inregistrariErpFaraCorespondent.map((e) => (
                      <TableRow key={e.id}>
                        <TableCell>{e.type === 'Incasare' ? 'Încasare' : 'Plată'}</TableCell>
                        <TableCell>{e.number}</TableCell><TableCell>{e.partnerName}</TableCell><TableCell>{e.date}</TableCell>
                        <TableCell align="right">{e.amount.toFixed(2).replace('.', ',')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Tranzacții bancare neînregistrate în ERP ({data.tranzactiiBancareNeinregistrate.length})</Typography>
            {data.tranzactiiBancareNeinregistrate.length === 0
              ? <Typography variant="body2" color="text.secondary">Nicio linie de extras nerezolvată în această perioadă.</Typography>
              : (
                <Table size="small">
                  <TableHead><TableRow><TableCell>Dată</TableCell><TableCell>Descriere</TableCell><TableCell align="right">Sumă</TableCell></TableRow></TableHead>
                  <TableBody>
                    {data.tranzactiiBancareNeinregistrate.map((l) => (
                      <TableRow key={l.lineId}>
                        <TableCell>{l.transactionDate}</TableCell><TableCell>{l.description}</TableCell>
                        <TableCell align="right">{l.amount.toFixed(2).replace('.', ',')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
          </Paper>

          {data.tranzactiiCategorisite.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Tranzacții categorisite ({data.tranzactiiCategorisite.length})</Typography>
              <Table size="small">
                <TableHead><TableRow><TableCell>Dată</TableCell><TableCell>Descriere</TableCell><TableCell>Categorie</TableCell><TableCell align="right">Sumă</TableCell></TableRow></TableHead>
                <TableBody>
                  {data.tranzactiiCategorisite.map((l) => (
                    <TableRow key={l.lineId}>
                      <TableCell>{l.transactionDate}</TableCell><TableCell>{l.description}</TableCell>
                      <TableCell>{BANK_TRANSACTION_CATEGORY_LABEL[l.category]}</TableCell>
                      <TableCell align="right">{l.amount.toFixed(2).replace('.', ',')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}
    </Stack>
  )
}
